import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { isEnterWithoutComposing } from '@/common/util/keyboard'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH = 'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'

export default function AIPurchaseAssistConversations() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState(null)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [evaluation, setEvaluation] = useState('')
  const [problemOnly, setProblemOnly] = useState(false)
  // 管理画面の対話テストで作られた会話。既定では一覧・KPI・CSVから外す
  const [includeTest, setIncludeTest] = useState(false)
  const [hasPurchase, setHasPurchase] = useState('')
  const [matchRoute, setMatchRoute] = useState('')
  const [hasOrderData, setHasOrderData] = useState(true)
  const [days, setDays] = useState(30)
  const [selectedId, setSelectedId] = useState(null)
  const [view, setView] = useState('list')      // list=一覧 / thread=スレッド閲覧
  const [analysis, setAnalysis] = useState(null)
  const [showAnalysis, setShowAnalysis] = useState(false)

  // テスト会話の扱いは一覧・KPI・比較・CSVで揃える（片方だけ数字が食い違わないように）
  const testQs = includeTest ? '&include_test=true' : ''

  const fetchSummary = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/conversations-summary?days=${days}${testQs}`, { headers: authHeaders() })
      if (r.ok) setSummary(await r.json())
    } catch (e) {/* ignore */}
  }

  const fetchAnalysis = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/conversations-analysis?days=${days}${testQs}`, { headers: authHeaders() })
      if (r.ok) setAnalysis(await r.json())
    } catch (e) {/* ignore */}
  }

  const exportCsv = async (format) => {
    const r = await fetch(
      `/api/sites/${siteId}/purchase-assist/conversations/export?days=${days}&format=${format}${testQs}`,
      { headers: authHeaders() },
    )
    if (!r.ok) { alert('エクスポート失敗'); return }
    const blob = await r.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${format === 'message' ? 'messages' : 'conversations'}_site${siteId}_${days}days.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const fetchAll = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage), days: String(days) })
      if (search.trim()) params.set('search', search.trim())
      if (evaluation) params.set('evaluation', evaluation)
      if (problemOnly) params.set('problem_only', 'true')
      if (hasPurchase) params.set('has_purchase', hasPurchase)
      if (matchRoute) params.set('match_route', matchRoute)
      if (includeTest) params.set('include_test', 'true')
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/conversations?${params}`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setItems(d.items || [])
      setTotal(d.total || 0)
      setHasOrderData(d.has_order_data !== false)
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [siteId, page, perPage, evaluation, problemOnly, hasPurchase, matchRoute, days, includeTest])
  useEffect(() => { fetchSummary() }, [siteId, days, includeTest])
  // 比較タブは開いている間だけ追従させる（閉じているのに取り直しても意味がない）
  useEffect(() => { if (showAnalysis) fetchAnalysis() }, [includeTest])

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='💬 会話履歴・分析'
        description='全会話の閲覧・検索・問題発見。PIIマスク済みのテキストで表示します。'
      />

      {/* 傾向サマリー（画面06仕様） */}
      {summary && (
        <div
          className={`${CARD} mb-3 flex flex-wrap items-center gap-4 p-3`}
          style={{ borderLeft: '4px solid var(--color-fill-brand)' }}
        >
          <div>
            <span className='text-secondary text-[10px]'>総会話 ({summary.period_days}日)</span>
            <div className='text-primary text-lg font-bold'>{summary.total_conversations.toLocaleString()}</div>
          </div>
          <Sep />
          <div>
            <span className='text-secondary text-[10px]'>👍率</span>
            <div className='text-success text-lg font-bold'>{summary.satisfaction_rate.toFixed(1)}%</div>
            <div className='text-tertiary text-[9px]'>👍{summary.thumbs_up_count}/👎{summary.thumbs_down_count}</div>
          </div>
          <Sep />
          <div>
            <span className='text-secondary text-[10px]'>「分かりません」率</span>
            <div className={`text-lg font-bold ${summary.cant_answer_rate > 10 ? 'text-critical' : 'text-warning'}`}>{summary.cant_answer_rate.toFixed(2)}%</div>
          </div>
          <Sep />
          <div>
            <span className='text-secondary text-[10px]'>購買率</span>
            <div className='text-emphasis text-lg font-bold'>{summary.purchase_rate.toFixed(2)}%</div>
            <div className='text-tertiary text-[9px]'>購買{summary.purchase_count}件</div>
          </div>
          <Sep />
          <div>
            <span className='text-secondary text-[10px]'>問題フラグ</span>
            <div className={`text-lg font-bold ${summary.problem_count > 0 ? 'text-critical' : 'text-secondary'}`}>{summary.problem_count}件</div>
          </div>
          <div className='ml-auto flex flex-wrap gap-1.5'>
            <Button color={ButtonColor.SECONDARY} onClick={() => exportCsv('conversation')}>📥 CSV（会話単位）</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => exportCsv('message')}>📥 CSV（メッセージ単位）</Button>
          </div>
        </div>
      )}

      {/* フィルター */}
      <div className={`${CARD} mb-3 flex flex-wrap items-center gap-2 p-3`}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (isEnterWithoutComposing(e)) { setPage(1); fetchAll() } }}
          placeholder='🔍 全文検索（発話・応答）'
          className={`${INPUT} min-w-[200px] flex-1`}
        />
        <select value={days} onChange={e => { setDays(parseInt(e.target.value)); setPage(1) }} className={`${INPUT} w-[110px]`}>
          <option value={7}>過去7日</option>
          <option value={30}>過去30日</option>
          <option value={90}>過去90日</option>
          <option value={365}>過去1年</option>
        </select>
        <select value={evaluation} onChange={e => { setEvaluation(e.target.value); setPage(1) }} className={`${INPUT} w-[130px]`}>
          <option value=''>評価すべて</option>
          <option value='thumbs_up'>👍 のみ</option>
          <option value='thumbs_down'>👎 のみ</option>
        </select>
        <select value={hasPurchase} onChange={e => { setHasPurchase(e.target.value); setPage(1) }} className={`${INPUT} w-[130px]`}>
          <option value=''>購買すべて</option>
          <option value='true'>購買あり</option>
          <option value='false'>購買なし</option>
        </select>
        <select
          value={matchRoute}
          onChange={e => { setMatchRoute(e.target.value); setPage(1) }}
          className={`${INPUT} w-[150px]`}
          title='受注との突合経路。突合は顧客IDまたは訪問者IDの一致のみで行います'
        >
          <option value=''>突合すべて</option>
          <option value='customer_id'>突合: 顧客ID</option>
          <option value='visitor_id'>突合: 訪問者ID</option>
          <option value='none'>突合なし</option>
        </select>
        <label className='text-secondary flex items-center gap-1 text-xs'>
          <input type='checkbox' checked={problemOnly} onChange={e => { setProblemOnly(e.target.checked); setPage(1) }} />
          問題フラグのみ
        </label>
        <label
          className='text-secondary flex items-center gap-1 text-xs'
          title='配信設定の対話テストで作られた会話。既定では一覧・KPI・CSVから除外しています'
        >
          <input type='checkbox' checked={includeTest} onChange={e => { setIncludeTest(e.target.checked); setPage(1) }} />
          テスト会話を含める
        </label>
        <Button color={ButtonColor.PRIMARY} onClick={() => { setPage(1); fetchAll() }}>検索</Button>
        <div className='flex gap-1.5'>
          {[['list', '📋 一覧'], ['thread', '💬 スレッド閲覧']].map(([k, label]) => (
            <button
              key={k} type='button' onClick={() => setView(k)}
              className={`cursor-pointer rounded-full border px-3 py-1 text-[11px] font-bold ${
                view === k
                  ? 'bg-surface-emphasis text-emphasis border-emphasis'
                  : 'bg-surface-secondary text-secondary border-secondary hover:bg-surface-hover'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <Button
          color={ButtonColor.SECONDARY}
          onClick={() => { setShowAnalysis(!showAnalysis); if (!analysis) fetchAnalysis() }}
        >
          📊 購入者/非購入者の比較
        </Button>
      </div>

      {showAnalysis && <PurchaseComparison analysis={analysis} days={days} onReload={fetchAnalysis} />}

      {error && <div className='bg-surface-critical text-critical mb-2.5 rounded-lg p-2.5 text-xs'>エラー: {error}</div>}

      {/* 一覧 / スレッド */}
      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : items.length === 0 ? (
        <div className={`${CARD} text-tertiary p-8 text-center text-xs`}>
          該当する会話がありません。
        </div>
      ) : view === 'thread' ? (
        <ThreadView siteId={siteId} items={items} authHeaders={authHeaders} />
      ) : (
        <>
          <div className={`${CARD} overflow-x-auto p-0`}>
            <table className='w-full border-collapse text-xs'>
              <thead>
                <tr className='bg-surface-secondary'>
                  <th className={TH}>ID</th>
                  <th className={TH}>日時</th>
                  <th className={TH}>初回質問</th>
                  <th className={TH}>流入ページ</th>
                  <th className={`${TH} text-center`}>ターン</th>
                  <th className={`${TH} text-center`}>評価</th>
                  <th className={`${TH} text-center`}>受注</th>
                  <th className={`${TH} text-center`}>A/B</th>
                  <th className={`${TH} text-center`}>コスト</th>
                  <th className={TH}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(c => (
                  <tr key={c.id} className={`border-b border-secondary ${c.problem_flag ? 'bg-surface-critical' : ''}`}>
                    <td className={`${TD} text-secondary font-mono`}>
                      #{c.id}
                      {c.is_test && <TestBadge />}
                    </td>
                    <td className={`${TD} text-secondary text-[10px]`}>{c.started_at ? new Date(c.started_at).toLocaleString('ja-JP') : '-'}</td>
                    <td className={`${TD} max-w-[260px] overflow-hidden text-ellipsis whitespace-nowrap`} title={c.first_question}>
                      {c.first_question || '-'}
                    </td>
                    <td className={`${TD} text-secondary max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap text-[10px]`} title={c.entry_page_url || ''}>
                      {c.entry_page_url || <span className='text-tertiary'>不明</span>}
                    </td>
                    <td className={`${TD} text-center`}>{c.turn_count != null ? c.turn_count : <span className='text-tertiary'>-</span>}</td>
                    <td className={`${TD} text-center`}>
                      {c.overall_evaluation === 'thumbs_up' ? '👍'
                        : c.overall_evaluation === 'thumbs_down' ? '👎'
                        : <span className='text-tertiary'>-</span>}
                      {c.problem_flag && <span className='text-critical ml-1'>⚠</span>}
                    </td>
                    <td className={`${TD} text-center`}>
                      <OrderCell order={c.order} hasOrderData={hasOrderData} />
                    </td>
                    <td className={`${TD} text-center`}>
                      {c.ab_variant
                        ? <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[9px] font-bold'>{c.ab_variant}</span>
                        : <span className='text-tertiary'>-</span>}
                    </td>
                    <td className={`${TD} text-secondary text-center text-[10px]`}>¥{((c.total_cost_usd || 0) * 150).toFixed(2)}</td>
                    <td className={TD}><Button color={ButtonColor.SECONDARY} onClick={() => setSelectedId(c.id)}>詳細</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* ページネーション */}
          <div className='mt-3 flex items-center justify-center gap-1.5 text-xs'>
            <Button color={ButtonColor.SECONDARY} onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>前へ</Button>
            <span className='text-secondary'>{page} / {Math.max(1, Math.ceil(total / perPage))} （計 {total} 件）</span>
            <Button color={ButtonColor.SECONDARY} onClick={() => setPage(page + 1)} disabled={page >= Math.ceil(total / perPage)}>次へ</Button>
          </div>
        </>
      )}

      {/* 詳細オーバーレイ */}
      {selectedId && <ConversationDetail siteId={siteId} convId={selectedId} authHeaders={authHeaders} onClose={() => setSelectedId(null)} />}
    </Main>
  )
}

export const MATCH_ROUTE_LABELS = { customer_id: '顧客ID', visitor_id: '訪問者ID' }

/** 対話テスト由来の会話であることを示すラベル。実訪問者の会話と取り違えないため */
function TestBadge() {
  return (
    <span
      className='bg-surface-secondary text-secondary border-secondary ml-1 rounded border px-1 py-px text-[9px] font-bold'
      title='配信設定の対話テストで作られた会話です'
    >
      テスト
    </span>
  )
}

/**
 * 受注セル。計測タグ未設置のときに「-」を出すと「買われなかった」と誤読されるため、
 * 「未計測」と区別して表示する。
 */
function OrderCell({ order, hasOrderData }) {
  if (!hasOrderData) return <span className='text-tertiary text-[10px]'>未計測</span>
  if (!order) return <span className='text-tertiary'>-</span>
  return (
    <span
      className='bg-surface-success text-success rounded px-1.5 py-px text-[9px] font-bold'
      title={`注文番号 ${order.order_number} / 突合 ${MATCH_ROUTE_LABELS[order.match_route] || order.match_route}`}
    >
      ✓ ¥{Number(order.gmv || 0).toLocaleString()}
      <span className='ml-1 font-normal'>{MATCH_ROUTE_LABELS[order.match_route] || order.match_route}</span>
    </span>
  )
}

/** 購入者／非購入者の比較。何を聞いた人が買い、何を聞いた人が買わなかったかを並べて見る */
function PurchaseComparison({ analysis, days, onReload }) {
  if (!analysis) {
    return <div className={`${CARD} text-secondary mb-3 p-4 text-center text-xs`}>集計中...</div>
  }
  if (!analysis.total) {
    return (
      <div className={`${CARD} text-tertiary mb-3 p-4 text-center text-xs`}>
        過去{days}日に会話がありません。
      </div>
    )
  }

  const Side = ({ title, data, accent }) => (
    <div className='flex-1'>
      <div className={`mb-1.5 text-xs font-bold ${accent}`}>{title}</div>
      <div className='text-secondary mb-2 text-[11px]'>
        会話 {data.conversation_count.toLocaleString()}件 / 平均 {data.avg_turns} ターン
      </div>
      <div className='text-secondary mb-1 text-[10px] font-semibold'>初回質問の頻出語</div>
      <div className='mb-2 flex flex-wrap gap-1'>
        {data.top_words.length === 0
          ? <span className='text-tertiary text-[10px]'>データなし</span>
          : data.top_words.map(w => (
            <span key={w.word} className='bg-surface-secondary text-primary rounded px-1.5 py-0.5 text-[10px]'>
              {w.word} <span className='text-tertiary'>{w.count}</span>
            </span>
          ))}
      </div>
      {Object.keys(data.opening_answers || {}).length > 0 && (
        <>
          <div className='text-secondary mb-1 text-[10px] font-semibold'>オープニング質問の回答</div>
          {Object.entries(data.opening_answers).map(([key, answers]) => (
            <div key={key} className='mb-1 text-[10px]'>
              <span className='text-tertiary'>{key}:</span>{' '}
              {answers.map(a => `${a.answer}(${a.count})`).join(' / ')}
            </div>
          ))}
        </>
      )}
      {data.top_entry_pages.length > 0 && (
        <>
          <div className='text-secondary mt-2 mb-1 text-[10px] font-semibold'>流入ページ</div>
          {data.top_entry_pages.slice(0, 5).map(p => (
            <div key={p.url} className='text-tertiary overflow-hidden text-ellipsis whitespace-nowrap text-[10px]' title={p.url}>
              {p.url} <span className='text-secondary'>{p.count}</span>
            </div>
          ))}
        </>
      )}
    </div>
  )

  return (
    <div className={`${CARD} mb-3 p-3`}>
      <div className='mb-2 flex items-center gap-2'>
        <span className='text-emphasis text-xs font-bold'>📊 購入者 / 非購入者の比較（過去{days}日・全{analysis.total}件）</span>
        <Button color={ButtonColor.SECONDARY} onClick={onReload}>↻ 再集計</Button>
      </div>
      <div className='flex flex-wrap gap-6'>
        <Side title='✓ 購買あり' data={analysis.purchased} accent='text-success' />
        <div className='bg-surface-secondary w-px' />
        <Side title='− 購買なし' data={analysis.not_purchased} accent='text-secondary' />
      </div>
    </div>
  )
}

/** スレッド閲覧：左に会話リスト、右に全ターン。開き直さずに読み進められる */
function ThreadView({ siteId, items, authHeaders }) {
  const [index, setIndex] = useState(0)
  const [data, setData] = useState(null)
  const current = items[index]

  useEffect(() => {
    if (!current) return
    setData(null)
    fetch(`/api/sites/${siteId}/purchase-assist/conversations/${current.id}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(setData)
      .catch(() => setData(null))
  }, [current?.id])

  if (!current) return null

  return (
    <div className='flex gap-3'>
      {/* 会話リスト */}
      <div className={`${CARD} max-h-[70vh] w-[280px] shrink-0 overflow-y-auto p-0`}>
        {items.map((c, i) => (
          <button
            key={c.id} type='button' onClick={() => setIndex(i)}
            className={`block w-full cursor-pointer border-b border-secondary p-2 text-left ${
              i === index ? 'bg-surface-emphasis' : 'hover:bg-surface-hover'}`}
          >
            <div className='flex items-center gap-1.5'>
              <span className='text-tertiary font-mono text-[10px]'>#{c.id}</span>
              {c.is_test && <TestBadge />}
              {c.order && <span className='text-success text-[10px]'>✓受注</span>}
              {c.overall_evaluation === 'thumbs_down' && <span className='text-critical text-[10px]'>👎</span>}
            </div>
            <div className='text-primary overflow-hidden text-ellipsis whitespace-nowrap text-[11px]'>
              {c.first_question || '(発話なし)'}
            </div>
          </button>
        ))}
      </div>

      {/* 選択中の会話 */}
      <div className={`${CARD} max-h-[70vh] flex-1 overflow-y-auto p-3`}>
        <div className='mb-2 flex flex-wrap items-center gap-2'>
          <span className='text-emphasis text-xs font-bold'>会話 #{current.id}</span>
          {current.is_test && <TestBadge />}
          <span className='text-tertiary text-[10px]'>{index + 1} / {items.length}</span>
          <div className='ml-auto flex gap-1.5'>
            <Button color={ButtonColor.SECONDARY} onClick={() => setIndex(Math.max(0, index - 1))} disabled={index <= 0}>← 前</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => setIndex(Math.min(items.length - 1, index + 1))} disabled={index >= items.length - 1}>次 →</Button>
          </div>
        </div>
        <div className='bg-surface-secondary text-secondary mb-2 rounded p-2 text-[10px]'>
          流入ページ: {current.entry_page_url || '不明'} / 入口: {current.entry_point || '不明'}
          {current.order && ` / ✓ 受注 ¥${Number(current.order.gmv || 0).toLocaleString()}（突合: ${MATCH_ROUTE_LABELS[current.order.match_route] || current.order.match_route}）`}
          {current.has_recommended_click && ' / ✓ 推薦クリック'}
        </div>
        {!data ? (
          <div className='text-secondary py-8 text-center text-xs'>読み込み中...</div>
        ) : (
          <div className='flex flex-col gap-2'>
            {data.messages.map(m => <MessageBubble key={m.id} m={m} />)}
          </div>
        )}
      </div>
    </div>
  )
}

/** 1メッセージの表示（詳細モーダルとスレッド閲覧で共用） */
function MessageBubble({ m }) {
  const opening = m.component_meta?.opening
  return (
    <div
      className={`rounded-lg p-2.5 ${m.role === 'user' ? 'bg-surface-emphasis' : 'bg-surface-success'}`}
      style={{ borderLeft: m.role === 'user' ? '3px solid var(--color-fill-brand)' : '3px solid var(--color-fill-success)' }}
    >
      <div className='text-secondary mb-1 text-[10px]'>
        {m.role === 'user' ? '👤 ユーザー' : '🤖 AI'}
        {m.llm_model && <span className='ml-2'>{m.llm_model}</span>}
        {m.latency_ms && <span className='ml-2'>{m.latency_ms}ms</span>}
        {m.cost_usd && <span className='ml-2'>¥{(m.cost_usd * 150).toFixed(3)}</span>}
        {m.user_evaluation === 'thumbs_up' && <span className='ml-2'>👍</span>}
        {m.user_evaluation === 'thumbs_down' && <span className='ml-2'>👎</span>}
        {m.page_url && <span className='text-tertiary ml-2'>{m.page_url}</span>}
      </div>
      <div className='text-primary text-size-13 whitespace-pre-wrap'>{m.content}</div>
      {opening && (
        <div className='text-secondary mt-1.5 rounded bg-surface p-1.5 text-[10px]'>
          オープニング質問の回答:{' '}
          {(opening.answers || []).map(a => `${a.question} → ${a.answer}`).join(' / ')}
        </div>
      )}
    </div>
  )
}

function ConversationDetail({ siteId, convId, authHeaders, onClose }) {
  const [data, setData] = useState(null)
  const [showNgInput, setShowNgInput] = useState(false)
  const [ngValue, setNgValue] = useState('')
  const [ngType, setNgType] = useState('ng_word')

  const reload = () => {
    fetch(`/api/sites/${siteId}/purchase-assist/conversations/${convId}`, { headers: authHeaders() })
      .then(r => r.json()).then(setData)
  }
  useEffect(() => { reload() }, [convId])

  const flagProblem = async () => {
    if (!window.confirm('この会話を問題報告としてフラグ付けします。よろしいですか？')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/conversations/${convId}/flag`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({}),
    })
    if (r.ok) { alert('問題報告を記録しました'); reload() }
    else alert('失敗')
  }

  const submitNg = async () => {
    if (!ngValue.trim()) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/conversations/${convId}/add-ng`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ng_value: ngValue.trim(), ng_rule_type: ngType, ng_action: 'block' }),
    })
    const d = await r.json()
    if (r.ok) {
      alert(d.duplicated ? 'すでに登録済みです' : `NGリストに追加しました (rule #${d.rule_id})`)
      setShowNgInput(false)
      setNgValue('')
    } else alert(`失敗: ${d.detail || r.status}`)
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-start justify-center p-8 pb-20' style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[90vh] w-full max-w-[820px] overflow-auto p-4`}>
        <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>
            💬 会話 #{convId}
            {data?.conversation?.is_test && <TestBadge />}
          </h3>
          <div className='flex flex-wrap gap-1.5'>
            <Button color={ButtonColor.SECONDARY} onClick={flagProblem}>⚠ 問題報告</Button>
            <Button color={ButtonColor.CRITICAL} onClick={() => setShowNgInput(!showNgInput)}>🚫 NGリストへ追加</Button>
            <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
          </div>
        </div>
        {showNgInput && (
          <div className='bg-surface-critical mb-3 rounded-lg border border-secondary p-2.5'>
            <div className='flex flex-wrap items-center gap-1.5'>
              <select value={ngType} onChange={e => setNgType(e.target.value)} className={`${INPUT} w-[130px]`}>
                <option value='ng_word'>NGワード</option>
                <option value='banned_topic'>禁止トピック</option>
              </select>
              <input value={ngValue} onChange={e => setNgValue(e.target.value)} placeholder='登録する値' className={`${INPUT} min-w-[200px] flex-1`} />
              <Button color={ButtonColor.PRIMARY} onClick={submitNg}>追加</Button>
              <Button color={ButtonColor.SECONDARY} onClick={() => { setShowNgInput(false); setNgValue('') }}>キャンセル</Button>
            </div>
          </div>
        )}
        {!data ? <div className='text-secondary py-8 text-center'>読み込み中...</div> : (
          <>
            <div className='bg-surface-emphasis text-secondary mb-3 rounded-lg p-2.5 text-[11px]'>
              <div>visitor: <code>{data.conversation.visitor_id}</code> | ターン: {data.conversation.turn_count} | コスト: ¥{((data.conversation.total_cost_usd || 0) * 150).toFixed(2)}</div>
              <div className='mt-1'>
                流入ページ: {data.conversation.entry_page_url || '不明'} / 入口: {data.conversation.entry_point || '不明'}
                {data.conversation.customer_id && ` / 顧客ID: ${data.conversation.customer_id}`}
              </div>
              <div className='mt-1'>
                {data.conversation.has_recommended_click && '✓ 推薦クリック'}
                {data.conversation.order
                  ? ` / ✓ 受注 ¥${Number(data.conversation.order.gmv || 0).toLocaleString()}（突合: ${MATCH_ROUTE_LABELS[data.conversation.order.match_route] || data.conversation.order.match_route}）`
                  : ' / 受注との突合なし'}
              </div>
            </div>

            {(data.orders || []).length > 0 && <OrderSection orders={data.orders} />}
            {(data.activities || []).length > 0 && <ActivityTimeline activities={data.activities} />}

            <div className='mt-3 flex flex-col gap-2'>
              {data.messages.map(m => <MessageBubble key={m.id} m={m} />)}
            </div>
            {data.llm_logs.length > 0 && (
              <details className='mt-3.5'>
                <summary className='text-emphasis cursor-pointer text-xs'>📊 LLMログ ({data.llm_logs.length}件)</summary>
                <pre className='bg-surface-secondary text-secondary mt-1.5 overflow-x-auto rounded p-2.5 text-[10px]'>
                  {JSON.stringify(data.llm_logs, null, 2)}
                </pre>
              </details>
            )}
          </>
        )}
      </div>
    </ModalOverlay>
  )
}

/** 突合できた受注の内訳。明細のどれが推薦・クリックに該当したかを示す */
function OrderSection({ orders }) {
  return (
    <div className='bg-surface-success mt-3 rounded-lg border border-secondary p-2.5'>
      <div className='text-primary mb-1.5 text-[11px] font-bold'>🧾 受注</div>
      {orders.map(o => (
        <div key={o.id} className='mb-2 last:mb-0'>
          <div className='text-secondary text-[11px]'>
            <code>{o.order_number}</code>
            <span className='ml-2'>¥{Number(o.total_amount || 0).toLocaleString()}</span>
            <span className='text-tertiary ml-2'>
              {o.ordered_at ? new Date(o.ordered_at).toLocaleString('ja-JP') : ''}
            </span>
            <span className='bg-surface-emphasis text-emphasis ml-2 rounded px-1.5 py-px text-[9px] font-bold'>
              突合: {MATCH_ROUTE_LABELS[o.match_route] || o.match_route}
            </span>
          </div>
          <table className='mt-1 w-full border-collapse text-[11px]'>
            <thead>
              <tr className='bg-surface-secondary'>
                <th className={TH}>商品ID</th>
                <th className={`${TH} text-right`}>単価</th>
                <th className={`${TH} text-right`}>数量</th>
                <th className={TH}>該当</th>
              </tr>
            </thead>
            <tbody>
              {o.items.map((it, i) => (
                <tr key={i} className='border-b border-secondary'>
                  <td className={`${TD} font-mono`}>{it.entity_id}</td>
                  <td className={`${TD} text-right`}>¥{Number(it.unit_price || 0).toLocaleString()}</td>
                  <td className={`${TD} text-right`}>{it.quantity}</td>
                  <td className={TD}>
                    {it.is_clicked
                      ? <span className='text-success font-bold'>クリック経由</span>
                      : it.is_recommended
                        ? <span className='text-emphasis'>推薦経由</span>
                        : <span className='text-tertiary'>推薦外</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

const ACTIVITY_ICONS = {
  conversation_start: '▶️',
  message_user: '🗣',
  message_assistant: '🤖',
  recommend_click: '👆',
  order: '🧾',
}

/** 利用行動・発話・受注を1本の時系列で並べる */
function ActivityTimeline({ activities }) {
  return (
    <details className='mt-3' open>
      <summary className='text-emphasis cursor-pointer text-xs font-bold'>
        🕒 行動の時系列（{activities.length}件）
      </summary>
      <ol className='mt-1.5 list-none border-l border-secondary pl-3'>
        {activities.map((a, i) => {
          const d = a.detail || {}
          const note = d.text || d.entity_id || d.order_number || d.entry_point || ''
          return (
            <li key={i} className='relative mb-1.5 text-[11px]'>
              <span className='text-tertiary mr-1.5 text-[10px]'>
                {a.at ? new Date(a.at).toLocaleString('ja-JP') : ''}
              </span>
              <span className='mr-1'>{ACTIVITY_ICONS[a.kind] || '•'}</span>
              <span className='text-secondary'>{a.label}</span>
              {note && <span className='text-primary ml-1.5'>{String(note).slice(0, 80)}</span>}
            </li>
          )
        })}
      </ol>
    </details>
  )
}

function Sep() {
  return <div className='bg-surface-secondary h-8 w-px' />
}
